import {
  PatchApplicationActivityDto,
  PatchApplicationActivityTypeDto,
  PatchApplicationDto,
  PatchApplicationScheduledActivityStatusDto,
  PatchApplicationScheduledActivityTypeDto,
  PatchLocationDto, UserDto
} from "server-openapi";
import {PatchApplicationStatusDto} from "server-openapi/dist/generated-sdk/api";
import {DateUtils} from "../../core/utils/dateUtils";
import {PatchLocationDTO, PatchLocations} from "../../pages/ResidentDetails/components/patches/PatchLocations";
import {UserUtils} from "./UserUtils";

export function mergePatchApplications(fromServer: PatchApplicationDto, localCopy: PatchApplicationDto): PatchApplicationDto 
{
  const result: PatchApplicationDto = {...fromServer};
  localCopy.activities ??= [];
  localCopy.activities?.forEach(activity => {
    // If the activity is not on the server, then add it from the client.
    if (!fromServer.activities?.some(x => x.clinicalSystemId === activity.clinicalSystemId)) {
      result.clientUpdatedAt = activity.clientUpdatedAt;
      result.activities ??= [];
      result.activities!.push(activity);
      if (activity.patchLocation) {
        result.patchLocation = {...activity.patchLocation};
      }
      if (activity.status) {
        result.status = activity.status;
      }
      if (activity.scheduledEnd) {
        result.scheduledEnd = activity.scheduledEnd;
      }
      if (activity.scheduledActivityId) {
        const scheduledActivity = result.scheduledActivities?.find(x => x.hsId === activity.scheduledActivityId);
        if (scheduledActivity) {
          scheduledActivity.status = PatchApplicationScheduledActivityStatusDto.Performed;
        }
      }
      if (activity.activityType === PatchApplicationActivityTypeDto.Applied) {
        if (localCopy.patchDrugAdministrations && localCopy.patchDrugAdministrations.length > 0) {
          result.firstAppliedPatchDrugAdministration = {...localCopy.patchDrugAdministrations[0]};
          result.lastAppliedPatchDrugAdministration = {...localCopy.patchDrugAdministrations[localCopy.patchDrugAdministrations.length - 1]};
        }
        result.firstAppliedAt = activity.clientCreatedAt;
        result.lastAppliedAt = activity.clientCreatedAt;
        result.status = PatchApplicationStatusDto.Applied;
      }
      else if (activity.activityType === PatchApplicationActivityTypeDto.Reapplied) {
        // Update the last applied at if it is a re-application.
        if (localCopy.patchDrugAdministrations && localCopy.patchDrugAdministrations.length > 0) {
          result.patchDrugAdministrations ??= [];
          for(const administration of localCopy.patchDrugAdministrations) {
            if (!result.patchDrugAdministrations.some(x => x.administeredDrugClinicalSystemId === administration.administeredDrugClinicalSystemId)) {
              result.patchDrugAdministrations.push({...administration});
            }
          }
          result.lastAppliedPatchDrugAdministration = {...localCopy.patchDrugAdministrations[localCopy.patchDrugAdministrations.length - 1]};
          result.status = PatchApplicationStatusDto.Reapplied;
        }
        result.lastAppliedAt = activity.clientCreatedAt;
        result.status = PatchApplicationStatusDto.Reapplied;
      }
      else if (activity.activityType === PatchApplicationActivityTypeDto.Cancelled) {
        result.active = false;
      }
      else if (activity.activityType === PatchApplicationActivityTypeDto.Removed) {
        result.status = PatchApplicationStatusDto.Removed;
      }
      else if (activity.activityType === PatchApplicationActivityTypeDto.FallenOff) {
        result.status = PatchApplicationStatusDto.FallenOff;
      }
      
      // Always update the comment from the latest activity, even if it's blank, as any previous entered one may no
      // longer apply to the current state of the run when a new activity is recorded.
      result.comment = activity.comment;
    }
  });
  return result;
}

export function getLastPatchApplication(patchApplication: PatchApplicationDto) : PatchApplicationActivityDto | undefined {
  if (!patchApplication.activities) {
    return undefined;
  }
  const applications = patchApplication.activities.filter(x => x.activityType === PatchApplicationActivityTypeDto.Applied || x.activityType === PatchApplicationActivityTypeDto.Reapplied);
  if (applications.length === 0) {
    return undefined;
  }
  return applications[applications.length - 1];
}
export function getLastPatchApplicationOperation(patchApplication: PatchApplicationDto) : PatchApplicationActivityDto | undefined {
  if (!patchApplication.activities) {
    return undefined;
  }
  const applications = patchApplication.activities.filter(x => x.activityType === PatchApplicationActivityTypeDto.Applied || x.activityType === PatchApplicationActivityTypeDto.Reapplied || x.activityType === PatchApplicationActivityTypeDto.Removed || x.activityType === PatchApplicationActivityTypeDto.FallenOff);
  if (applications.length === 0) {
    return undefined;
  }
  return applications[applications.length - 1];
}
export function getPatchApplicationAppliedText(userUtils: UserUtils, patchApplication: PatchApplicationDto) : string | undefined {
  const applicationActivity = getLastPatchApplicationOperation(patchApplication);
  if (!applicationActivity) {
    return undefined;
  }

  return getPatchApplicationDescription(userUtils, applicationActivity.activityType, applicationActivity.clientUpdatedAt, applicationActivity.clientUser);
}
export function getPatchLocationForApplication(location: PatchLocationDto | undefined) : PatchLocationDTO | undefined {
  return PatchLocations.find(x => x.LocationNumber.toString() === location?.number);
}

export function getPatchApplicationDescription(userUtils: UserUtils, activityType: PatchApplicationStatusDto | PatchApplicationActivityTypeDto | PatchApplicationScheduledActivityTypeDto | undefined, at: string | undefined, clientUser: UserDto | undefined) {

  switch (activityType) {
  case PatchApplicationActivityTypeDto.Sighted:
    return `Patch Sighted at ${DateUtils.toDisplayString(at)} by ${userUtils.getUserName(clientUser)}.`;
  case PatchApplicationScheduledActivityTypeDto.Sight:
    return  `No sighting added.`
  case PatchApplicationActivityTypeDto.Applied:
  case PatchApplicationStatusDto.Applied:
    return `Patch applied at ${DateUtils.toDisplayString(at)} by ${userUtils.getUserName(clientUser)}.`;
  case  PatchApplicationActivityTypeDto.Reapplied:
  case  PatchApplicationStatusDto.Reapplied:
    return `Patch reapplied at ${DateUtils.toDisplayString(at)} by ${userUtils.getUserName(clientUser)}`;
  case  PatchApplicationActivityTypeDto.Removed:
  case  PatchApplicationStatusDto.Removed:
    return `Patch removed at ${DateUtils.toDisplayString(at)} by ${userUtils.getUserName(clientUser)}`;
  case  PatchApplicationActivityTypeDto.FallenOff:
  case  PatchApplicationStatusDto.FallenOff:
    return `Patch fell off at ${DateUtils.toDisplayString(at)} by ${userUtils.getUserName(clientUser)}`;
  case PatchApplicationScheduledActivityTypeDto.Remove:
    return  `Patch due for removal.`
  default:
    return "";
  }
}
